import { AnalyticsMembershipEvent } from '@fruitful/types';
import { useCallback } from 'react';

import { requestBuilder } from '../../http/RequestBuilder';
import { trackForMembership } from '../../logger/Logger';
import { useUser } from '../../user/providers/UserProvider';

interface PatchUserGuideBody {
    guideId: string;
}

interface PatchUserGuideUrlParams {
    userId: string;
}

const usePatchUserGuide = requestBuilder.mutation<
    unknown,
    PatchUserGuideBody,
    PatchUserGuideUrlParams
>({
    endpoint: ({ userId }) => `/api/user/${userId}/guide`,
    method: 'PATCH',
});

export const useSwitchMembershipGuide = () => {
    const { user, refetch: refetchUser } = useUser();
    const {
        mutateAsync: patchUserGuide,
        isPending,
        isSuccess,
        isError,
        error,
    } = usePatchUserGuide();

    const switchMembershipGuide = useCallback(
        async ({
            originalGuideId,
            newGuideId,
            shouldRefetchUser = true,
        }: {
            originalGuideId: string;
            newGuideId: string;
            shouldRefetchUser?: boolean;
        }) => {
            await patchUserGuide(
                {
                    body: { guideId: newGuideId },
                    urlParams: { userId: user?.id ?? '' },
                },
                {
                    onSuccess: () => {
                        if (shouldRefetchUser) {
                            void refetchUser?.();
                        }
                    },
                },
            );
            trackForMembership(
                AnalyticsMembershipEvent.MEMBERSHIP_GUIDE_SWITCHED,
                {
                    originalGuideId,
                    newGuideId,
                },
            );
        },
        [patchUserGuide, refetchUser],
    );

    return {
        switchMembershipGuide,
        isSwitchingMembershipGuide: isPending,
        isSwitchMembershipGuideSuccess: isSuccess,
        isSwitchMembershipGuideError: isError,
        switchMembershipGuideError: error,
    };
};
